import BaseLayout from '@/components/layout/BaseLayout.vue';
import { isDevelopment } from '@/config';
import { TENANT_MODULES } from '@/models/modules';
import { Sentry } from '@/plugins/sentry';
import { watchBackButton } from '@/util/back-button';
import { authGuard } from '@/util/router-guard';
import { paths } from '@/util/paths';
import { viewport } from '@/util/viewport';
import PageBIGraph from '@/views/business-intelligence/PageBIGraph.vue';
import PageBusinessIntelligence from '@/views/business-intelligence/PageBusinessIntelligence.vue';
import PageBIPerformance from '@/views/business-intelligence/PageBIPerformance.vue';
import PageBICompetition from '@/views/business-intelligence/PageBICompetition.vue';
import PageBICompetitorPerformance from '@/views/business-intelligence/PageBICompetitorPerformance.vue';
import PageBIEmitters from '@/views/business-intelligence/PageBIEmitters.vue';
import PageBIEmitterPerformance from '@/views/business-intelligence/PageBIEmitterPerformance.vue';
import PageOpportunityAnalysis from '@/views/PageOpportunityAnalysis';
import PageOpportunityEstimation from '@/views/PageOpportunityEstimation';
import PageOpportunityForms from '@/views/PageOpportunityForms';
import PageOpportunityFormReview from '@/views/PageOpportunityFormReview';
import PageOpportunityGoNoGo from '@/views/PageOpportunityGoNoGo';
import Vue from 'vue';
import Router from 'vue-router';
import ActivityBasedTimer from './services/activity-based-timer.service';
import store from './store/store';
import PageEmailConfirmation from './views/PageEmailConfirmation.vue';
import PageExplore from './views/PageExplore.vue';
import PageContactDirectory from './views/PageContactDirectory.vue';
import PageForbiddenPlatform from './views/PageForbiddenPlatform.vue';
import PageForgotPassword from './views/PageForgotPassword.vue';
import PageInvite from './views/PageInvite.vue';
import PageKeywords from './views/PageKeywords.vue';
import PageNotifications from './views/PageNotifications.vue';
import PageLogin from './views/PageLogin.vue';
import PageNotFound from './views/PageNotFound.vue';
import PageOpportunities from './views/PageOpportunities.vue';
import PageOpportunityDetails from './views/PageOpportunityDetails.vue';
import PagePreProjects from './views/PagePreProjects.vue';
import PageResetPassword from './views/PageResetPassword.vue';
import PageResetPasswordError from './views/PageResetPasswordError.vue';
import PageTenderDetails from './views/PageTenderDetails.vue';
import PageTenders from './views/PageTenders.vue';
import PageAdmin from './views/admin/PageAdmin.vue';
import PageAdminBilling from './views/admin/PageAdminBilling.vue';
import PageAdminDocumentKeywords from './views/admin/PageAdminDocumentKeywords.vue';
import PageAdminTenantProfile from './views/admin/PageAdminTenantProfile.vue';
import PageAdminInvites from './views/admin/PageAdminInvites.vue';
import PageAdminKeywords from './views/admin/PageAdminKeywords.vue';
import PageAdminModules from './views/admin/PageAdminModules.vue';
import PageAdminPlatforms from './views/admin/PageAdminPlatforms.vue';
import PageAdminPlatformCatalog from './views/admin/PageAdminPlatformCatalog.vue';
import PageAdminScraperHealth from './views/admin/PageAdminScraperHealth.vue';
import PageAdminTenants from './views/admin/PageAdminTenants.vue';
import PageAdminUsers from './views/admin/PageAdminUsers.vue';
import PageAdminUserProfile from './views/admin/PageAdminUserProfile.vue';
import PageAdminUserNotifications from './views/admin/PageAdminUserNotifications.vue';
import PageAdminOpportunities from '@/views/admin/PageAdminOpportunities.vue';
import PageAdminOpportunityLabels from '@/views/admin/PageAdminOpportunityLabels.vue';
import PageOpportunityQuotes from '@/views/PageOpportunityQuotes.vue';
import PageAdminTenant from './views/admin/PageAdminTenant.vue';
import PageAdminGoNoGo from './views/admin/PageAdminGoNoGo.vue';
import PageAdminEvaluationGrid from './views/admin/PageAdminEvaluationGrid.vue';
import PageAdminTaskTemplates from './views/admin/PageAdminTaskTemplates.vue';
import PageAdminSyncProfiles from './views/admin/PageAdminSyncProfiles.vue';
import PageAdminSyncProfileFieldMappings from './views/admin/PageAdminSyncProfileFieldMappings.vue';
import PageAdminOpportunitySettings from './views/admin/PageAdminOpportunitySettings.vue';
import PageAdminTerminology from './views/admin/PageAdminTerminology.vue';
import PageAdminPreProjectsImport from './views/admin/PageAdminPreProjectsImport.vue';
import PageOpportunityTasks from '@/views/PageOpportunityTasks.vue';

Vue.use(Router);

const versioningTimer = new ActivityBasedTimer();

let previousRoute = null;

const router = new Router({
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return { selector: to.hash };
    }

    if (savedPosition) {
      return savedPosition;
    }

    return { x: 0, y: 0 };
  },
});

watchBackButton(router);

router.addRoutes([
  {
    path: paths.root,
    name: 'home',
    redirect: paths.tendersInbox,
    component: BaseLayout,
    children: [
      {
        path: paths.tenders,
        name: 'tenders',
        redirect: paths.tendersInbox,
        component: PageTenders,
      },
      {
        path: paths.tendersInbox,
        name: 'tenders-inbox',
        component: PageTenders,
        meta: {
          title: 'title.tenders',
        },
      },
      {
        path: paths.opportunities,
        name: 'opportunities',
        component: PageOpportunities,
        meta: {
          title: 'title.opportunities',
          permissions: [TENANT_MODULES.opportunities],
        },
      },
      {
        path: paths.businessIntelligence,
        name: 'business-intelligence',
        component: PageBusinessIntelligence,
        redirect: paths.businessIntelligenceDashboard,
        meta: {
          title: 'title.business-intelligence',
          permissions: [TENANT_MODULES.business_intelligence],
          upsellOnMissing: true,
        },
        // The guard checks only the final route's meta.permissions, so each
        // child below repeats the module it needs; the parent's upsellOnMissing
        // still applies (the guard looks for it on every matched route).
        children: [
          {
            path: paths.businessIntelligenceDashboard,
            name: 'business-intelligence-performance',
            component: PageBIPerformance,
            meta: {
              title: 'title.business-intelligence-performance',
              permissions: [TENANT_MODULES.business_intelligence],
            },
          },
          {
            path: paths.businessIntelligenceCompetition,
            name: 'business-intelligence-competition',
            component: PageBICompetition,
            meta: {
              title: 'title.business-intelligence-competition',
              permissions: [TENANT_MODULES.business_intelligence],
            },
          },
          {
            path: paths.businessIntelligenceCompetitorPerformance,
            name: 'business-intelligence-competitor-performance',
            component: PageBICompetitorPerformance,
            meta: {
              name: 'title.business-intelligence-competition',
              permissions: [TENANT_MODULES.business_intelligence],
            },
          },
          {
            path: paths.businessIntelligenceEmitters,
            name: 'business-intelligence-emitters',
            component: PageBIEmitters,
            meta: {
              title: 'title.business-intelligence-emitters',
              permissions: [TENANT_MODULES.business_intelligence],
            },
          },
          {
            path: paths.businessIntelligenceEmitterPerformance,
            name: 'business-intelligence-emitter-performance',
            component: PageBIEmitterPerformance,
            meta: {
              title: 'title.business-intelligence-emitters',
              permissions: [TENANT_MODULES.business_intelligence],
            },
          },
          {
            path: paths.businessIntelligenceGraph,
            name: 'business-intelligence-graph',
            component: PageBIGraph,
            meta: {
              title: 'title.bi-dashboard',
              permissions: [TENANT_MODULES.bi],
            },
          },
        ],
      },
      {
        path: paths.explore,
        name: 'explore',
        component: PageExplore,
        meta: {
          title: 'title.explore',
        },
      },
      {
        path: paths.preProjects,
        name: 'pre-projects',
        component: PagePreProjects,
        meta: {
          title: 'title.pre-projects',
          permissions: [TENANT_MODULES.pre_projects],
          upsellOnMissing: true,
        },
      },
      {
        path: paths.contactDirectory,
        name: 'contact-directory',
        component: PageContactDirectory,
        meta: {
          title: 'title.contact-directory',
          permissions: [TENANT_MODULES.contact_directory],
        },
      },
      {
        // Where the bell leads on phones; larger screens open a panel under it instead.
        path: paths.notifications,
        name: 'notifications',
        component: PageNotifications,
        meta: {
          title: 'title.notifications',
        },
      },
      {
        path: paths.keywords,
        name: 'keywords',
        component: PageKeywords,
        meta: {
          title: 'title.keywords',
        },
      },
      {
        path: paths.tenderDetails,
        name: 'tender-details',
        component: PageTenderDetails,
        meta: {
          title: 'title.tender-details',
        },
        props: true,
      },
      {
        path: paths.opportunityDetails,
        name: 'opportunity-details',
        component: PageOpportunityDetails,
        meta: {
          title: 'title.opportunity-details',
          permissions: [TENANT_MODULES.opportunities],
        },
        props: true,
      },
      {
        path: paths.opportunityAnalysis,
        name: 'opportunity-analysis',
        component: PageOpportunityAnalysis,
        meta: {
          title: 'title.opportunities',
          permissions: [TENANT_MODULES.opportunities, TENANT_MODULES.documents],
        },
        props: true,
      },
      {
        path: paths.opportunityForms,
        name: 'opportunity-forms',
        component: PageOpportunityForms,
        meta: {
          title: 'title.opportunity-forms',
          permissions: [TENANT_MODULES.opportunities, TENANT_MODULES.documents, TENANT_MODULES.form_filling],
        },
        props: true,
      },
      {
        path: paths.opportunityFormReview,
        name: 'opportunity-form-review',
        component: PageOpportunityFormReview,
        meta: {
          title: 'title.opportunity-forms',
          permissions: [TENANT_MODULES.opportunities, TENANT_MODULES.documents, TENANT_MODULES.form_filling],
        },
        props: true,
      },
      {
        path: paths.opportunityGoNoGo,
        name: 'opportunity-go-nogo',
        component: PageOpportunityGoNoGo,
        meta: {
          title: 'title.go-nogo',
          permissions: [TENANT_MODULES.opportunities, TENANT_MODULES.gonogo],
        },
        props: true,
      },
      {
        path: paths.opportunityEstimation,
        name: 'opportunity-estimation',
        component: PageOpportunityEstimation,
        meta: {
          title: '',
          permissions: [TENANT_MODULES.opportunities, TENANT_MODULES.documents, TENANT_MODULES.slip_extraction],
        },
        props: true,
      },
      {
        path: paths.opportunityQuotes,
        name: 'opportunity-quotes',
        component: PageOpportunityQuotes,
        meta: {
          title: 'title.quotes',
          permissions: [TENANT_MODULES.opportunities, TENANT_MODULES.quotes],
        },
        props: true,
      },
      {
        path: paths.opportunityTasks,
        name: 'opportunity-tasks',
        component: PageOpportunityTasks,
        meta: {
          title: 'title.tasks',
          permissions: [TENANT_MODULES.opportunities, TENANT_MODULES.tasks],
        },
        props: true,
      },
      {
        path: paths.admin,
        name: 'admin',
        // Phones list the settings here; larger screens show a page beside that list.
        beforeEnter: (to, from, next) =>
          to.name === 'admin' && viewport.size !== 'phone' ? next(paths.adminInvites) : next(),
        component: PageAdmin,
        children: [
          {
            path: paths.adminUserProfile,
            name: 'admin-user-profile',
            component: PageAdminUserProfile,
          },
          {
            path: paths.adminUserNotifications,
            name: 'admin-user-notifications',
            component: PageAdminUserNotifications,
          },
          {
            path: paths.adminInvites,
            name: 'admin-invite',
            component: PageAdminInvites,
            meta: {
              manager: true,
            },
          },
          {
            path: paths.adminBilling,
            name: 'admin-billing',
            component: PageAdminBilling,
            meta: {
              manager: true,
            },
          },
          {
            path: paths.adminUsers,
            name: 'admin-users',
            component: PageAdminUsers,
          },
          {
            path: paths.adminOpportunityManagement,
            name: 'admin-opportunity-management',
            component: PageAdminOpportunities,
            meta: {
              manager: true,
              permissions: [TENANT_MODULES.opportunities],
            },
          },
          {
            path: paths.adminOpportunityLabels,
            name: 'admin-opportunity-labels',
            component: PageAdminOpportunityLabels,
            meta: {
              manager: true,
              permissions: [TENANT_MODULES.opportunities],
            },
          },
          {
            path: paths.adminKeywords,
            name: 'admin-keywords',
            component: PageAdminKeywords,
            meta: {
              adminTenant: true,
            },
          },
          {
            path: paths.adminDocumentKeywords,
            name: 'admin-document-keywords',
            component: PageAdminDocumentKeywords,
            meta: {
              adminTenant: true,
              permissions: [TENANT_MODULES.opportunities, TENANT_MODULES.documents],
            },
          },
          {
            path: paths.adminTenantProfile,
            name: 'admin-tenant-profile',
            component: PageAdminTenantProfile,
            meta: {
              manager: true,
              permissions: [TENANT_MODULES.form_filling],
            },
          },
          {
            path: paths.adminPlatforms,
            name: 'admin-platforms',
            component: PageAdminPlatforms,
            meta: {
              adminTenant: true,
            },
          },
          {
            path: paths.adminModules,
            name: 'admin-modules',
            component: PageAdminModules,
            meta: {
              adminTenant: true,
            },
          },
          {
            path: paths.adminTenants,
            name: 'admin-tenants',
            component: PageAdminTenants,
            meta: {
              admin: true,
            },
          },
          {
            path: paths.adminScraperHealth,
            name: 'admin-scraper-health',
            // admin: true is the strictest guard (SYSTEM_ADMIN or ADMIN) and matches the
            // @PreAuthorize on the API. Tenant admins have no business pausing a scraper that every
            // tenant shares.
            component: PageAdminScraperHealth,
            meta: {
              admin: true,
            },
          },
          {
            path: paths.adminPlatformCatalog,
            name: 'admin-platform-catalog',
            // The catalog is shared by every tenant; the API only lets platform admins write it.
            component: PageAdminPlatformCatalog,
            meta: {
              admin: true,
            },
          },
          {
            path: paths.adminPreProjectsImport,
            name: 'admin-pre-projects-import',
            component: PageAdminPreProjectsImport,
            meta: {
              admin: true,
            },
          },
          {
            path: paths.adminGoNoGo,
            name: 'admin-go-nogo',
            component: PageAdminGoNoGo,
          },
          {
            path: paths.adminTenant,
            name: 'admin-tenant',
            component: PageAdminTenant,
          },
          {
            path: paths.adminEvaluationGrid,
            name: 'admin-evaluation-grid',
            component: PageAdminEvaluationGrid,
            meta: {
              manager: true,
            },
          },
          {
            path: paths.adminTaskTemplates,
            name: 'admin-task-templates',
            component: PageAdminTaskTemplates,
            meta: {
              manager: true,
            },
          },
          {
            path: paths.adminSyncProfiles,
            name: 'admin-sync-profiles',
            component: PageAdminSyncProfiles,
            meta: {
              manager: true,
            },
          },
          {
            path: paths.adminSyncProfileFieldMappings,
            name: 'admin-sync-profile-field-mappings',
            component: PageAdminSyncProfileFieldMappings,
            meta: {
              manager: true,
            },
          },
          {
            path: paths.adminOpportunitySettings,
            name: 'admin-opportunity-settings',
            component: PageAdminOpportunitySettings,
            meta: {
              manager: true,
            },
          },
          {
            path: paths.adminTerminology,
            name: 'admin-terminology',
            component: PageAdminTerminology,
            meta: {
              adminTenant: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: paths.login,
    name: 'login',
    component: PageLogin,
    meta: {
      public: true,
      onlyWhenLoggedOut: true,
      title: 'title.login',
    },
  },
  {
    path: paths.invite,
    name: 'invite',
    component: PageInvite,
    meta: {
      public: true,
    },
    props: true,
  },
  {
    path: paths.emailConfirmation,
    name: 'email-confirmation',
    component: PageEmailConfirmation,
    meta: {
      public: true,
    },
    props: true,
  },
  {
    path: paths.forgotPassword,
    name: 'forgot-password',
    component: PageForgotPassword,
    meta: {
      public: true,
      title: 'title.forgot-password',
    },
  },
  {
    path: paths.resetPassword,
    name: 'reset-password',
    component: PageResetPassword,
    meta: {
      public: true,
      title: 'title.reset-password',
    },
  },
  {
    path: paths.resetPasswordError,
    name: 'reset-password-error',
    component: PageResetPasswordError,
    meta: {
      public: true,
      title: 'title.reset-password',
    },
  },
  {
    path: paths.notFound,
    name: 'not-found',
    component: PageNotFound,
    meta: {
      title: 'title.not-found',
    },
  },
  {
    path: paths.forbiddenPlatform,
    name: 'forbidden-platform',
    component: PageForbiddenPlatform,
    meta: {
      title: 'title.forbidden-platform',
    },
    props: true,
  },
  {
    path: paths.support,
    name: 'support',
    beforeEnter() {
      location.href = 'mailto:support@aloha.app';
    },
    meta: {
      public: true,
    },
  },
  {
    path: '*',
    beforeEnter: (to, from, next) => {
      next(paths.notFound);
    },
  },
]);

if (isDevelopment()) {
  router.addRoutes([
    {
      path: '/showcase',
      name: 'showcase',
      // Lazy-loaded so the dev-only showcase is split into its own chunk and
      // never ends up in the production bundle (the route itself is dev-gated).
      component: () => import(/* webpackChunkName: "showcase" */ './views/PageShowcase.vue'),
      meta: {
        public: true,
      },
    },
    {
      path: paths.market,
      component: BaseLayout,
      children: [
        {
          path: '',
          name: 'market',
          component: () => import(/* webpackChunkName: "market" */ '@/views/PageMarket.vue'),
          meta: {
            title: 'title.market',
          },
        },
      ],
    },
    {
      path: paths.buyer,
      name: 'buyer',
      component: () => import(/* webpackChunkName: "market-modal" */ '@/views/PageMarketModal.vue'),
      meta: {
        title: 'title.market',
      },
    },
    {
      path: paths.seller,
      name: 'seller',
      component: () => import(/* webpackChunkName: "market-modal" */ '@/views/PageMarketModal.vue'),
      meta: {
        title: 'title.seller',
      },
    },
  ]);
}

router.beforeEach((to, from, next) => {
  versioningTimer.runTimersCheck();
  return authGuard(to, from, next);
});

// Backstop: should the guard ever reject despite its internal handling (or an
// async route component fail to load), don't leave the user on a blank page.
router.onError((error) => {
  Sentry.captureException(error, { context: 'router.onError' });
  console.error('Router navigation error:', error);
});

versioningTimer.setInterval({
  async callback() {
    await store.dispatch('appVersionDetector/checkIfVersionIsOutdated');
  },
  // Normal interval is once every 10 minutes
  interval: 1000 * 60 * 10,
  // Forced interval is once every 10 minutes
  forcedInterval: 1000 * 60 * 10,
});

export { getPreviousRoute } from '@/util/router-guard';

export default router;
